/**
 * Follow-ups tracker — surfaces every prospect waiting for a touch.
 *
 * Three buckets, in order of urgency:
 *   • Late      — next_action_at < today
 *   • Today     — next_action_at = today
 *   • This week — next_action_at within 7 days
 *
 * Each row offers "Marquer fait" (clears the date) and "+3j" (postpone),
 * plus a quick deep-link to the prospect drawer.
 */
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Loader2,
  BellRing,
  Check,
  Clock,
  CalendarPlus,
  ChevronRight,
} from 'lucide-react';
import { usePipeline, type Prospect } from '@/hooks/useProspect';
import { TEMPERATURE_META } from '@/lib/prospectMeta';

function isDueToday(iso: string): boolean {
  const d = new Date(iso);
  const today = new Date();
  return (
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  );
}

function daysFromNow(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.floor(ms / 86_400_000);
}

interface Buckets {
  late: Prospect[];
  today: Prospect[];
  thisWeek: Prospect[];
  later: Prospect[];
}

function bucketize(prospects: Prospect[]): Buckets {
  const buckets: Buckets = { late: [], today: [], thisWeek: [], later: [] };
  for (const p of prospects) {
    if (!p.next_action_at) continue;
    const days = daysFromNow(p.next_action_at);
    if (isDueToday(p.next_action_at)) buckets.today.push(p);
    else if (days < 0) buckets.late.push(p);
    else if (days <= 7) buckets.thisWeek.push(p);
    else buckets.later.push(p);
  }
  // Late first by oldest, then today by stage urgency.
  buckets.late.sort(
    (a, b) =>
      new Date(a.next_action_at!).getTime() - new Date(b.next_action_at!).getTime(),
  );
  buckets.thisWeek.sort(
    (a, b) =>
      new Date(a.next_action_at!).getTime() - new Date(b.next_action_at!).getTime(),
  );
  return buckets;
}

export default function FollowUpsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { board, isLoading, update } = usePipeline();

  const allProspects = board ? Object.values(board.board).flat() : [];
  const buckets = bucketize(allProspects);
  const totalDue = buckets.late.length + buckets.today.length;

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="animate-spin text-pink" size={28} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[860px] px-5 py-10 sm:px-8">
      <header className="mb-7 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3.5">
          <div>
            <p className="font-mono text-[11px] font-700 uppercase tracking-[0.18em] text-pink">
              Relances
            </p>
            <h1 className="font-display text-[clamp(20px,3vw,28px)] font-800 tracking-[-0.025em]">{t('followups.title')}</h1>
          </div>
        </div>
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-700"
          style={{
            background: totalDue > 0 ? 'rgba(245,158,11,0.12)' : 'rgba(16,185,129,0.10)',
            color: totalDue > 0 ? '#b45309' : '#047857',
          }}
        >
          <BellRing size={12} /> {totalDue} à faire
        </span>
      </header>

      {totalDue === 0 && buckets.thisWeek.length === 0 ? (
        <Empty onAudit={() => navigate('/audit')} />
      ) : (
        <div className="space-y-5">
          <Bucket
            label="En retard"
            tint="#ef4444"
            prospects={buckets.late}
            update={update}
            onOpen={(p) => navigate(`/prospects?open=${p.id}`)}
          />
          <Bucket
            label="Aujourd'hui"
            tint="#f59e0b"
            prospects={buckets.today}
            update={update}
            onOpen={(p) => navigate(`/prospects?open=${p.id}`)}
          />
          <Bucket
            label="Cette semaine"
            tint="#3b82f6"
            prospects={buckets.thisWeek}
            update={update}
            onOpen={(p) => navigate(`/prospects?open=${p.id}`)}
          />
          {buckets.later.length > 0 && (
            <Bucket
              label="Plus tard"
              tint="#94a3b8"
              prospects={buckets.later}
              update={update}
              onOpen={(p) => navigate(`/prospects?open=${p.id}`)}
              muted
            />
          )}
        </div>
      )}
    </div>
  );
}

function Empty({ onAudit }: { onAudit: () => void }) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-hi rounded-3xl p-10 text-center"
    >
      <span
        className="mx-auto mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl text-white"
        style={{ background: 'linear-gradient(135deg, #10b981, #3b82f6)' }}
      >
        <Check size={26} />
      </span>
      <h2 className="font-display text-xl font-800 tracking-[-0.02em]">{t('followups.allClear')}</h2>
      <p className="mx-auto mt-2 max-w-md text-[14px] leading-[1.6] text-ink/65">
        {t('followups.emptyDetail')}
      </p>
      <button onClick={onAudit} className="btn-primary mt-6">
        {t('followups.auditCta')}
      </button>
    </motion.div>
  );
}

function Bucket({
  label,
  tint,
  prospects,
  update,
  onOpen,
  muted,
}: {
  label: string;
  tint: string;
  prospects: Prospect[];
  update: ReturnType<typeof usePipeline>['update'];
  onOpen: (p: Prospect) => void;
  muted?: boolean;
}) {
  if (prospects.length === 0) return null;
  return (
    <section>
      <div className="mb-2.5 flex items-baseline justify-between">
        <h3 className="flex items-center gap-2 font-display text-[14px] font-800 tracking-[-0.015em]">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: tint }} />
          {label}
          <span className="font-mono text-[11px] font-700 text-ink/45">{prospects.length}</span>
        </h3>
      </div>
      <div className={`space-y-2.5 ${muted ? 'opacity-70' : ''}`}>
        {prospects.map((p, i) => (
          <FollowUpRow key={p.id} prospect={p} delay={i * 0.04} update={update} onOpen={onOpen} />
        ))}
      </div>
    </section>
  );
}

function FollowUpRow({
  prospect: p,
  delay,
  update,
  onOpen,
}: {
  prospect: Prospect;
  delay: number;
  update: ReturnType<typeof usePipeline>['update'];
  onOpen: (p: Prospect) => void;
}) {
  const { t } = useTranslation();
  const temp = p.temperature ? TEMPERATURE_META[p.temperature] : null;
  const name = p.display_name ?? p.handle ?? 'Prospect';
  const dueLabel = p.next_action_at ? formatDue(p.next_action_at) : '';

  function markDone(): void {
    update.mutate({ id: p.id, patch: { next_action_at: null } });
  }
  function postpone(days: number): void {
    const next = new Date();
    next.setDate(next.getDate() + days);
    update.mutate({ id: p.id, patch: { next_action_at: next.toISOString() } });
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.3 }}
      className="card flex items-center gap-3 p-3.5"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <button
            onClick={() => onOpen(p)}
            className="truncate text-left font-display text-[14px] font-800 tracking-[-0.015em] text-ink transition hover:text-pink"
          >
            {name}
          </button>
          {temp && (
            <span
              className="rounded-full px-1.5 py-0.5 text-[10px] font-700"
              style={{ background: `${temp.color}1a`, color: temp.color }}
            >
              {temp.emoji} {t(temp.labelKey)}
            </span>
          )}
        </div>
        {p.next_action && (
          <p className="mt-0.5 truncate text-[12px] text-ink/60">{p.next_action}</p>
        )}
        <p className="mt-0.5 inline-flex items-center gap-1 font-mono text-[10.5px] font-700 uppercase tracking-[0.1em] text-ink/45">
          <Clock size={9} /> {dueLabel}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <button
          onClick={markDone}
          disabled={update.isPending}
          className="inline-flex items-center gap-1 rounded-lg bg-emerald-500/10 px-2.5 py-1.5 text-[11.5px] font-700 text-emerald-700 transition hover:bg-emerald-500/20 disabled:opacity-50"
          title={t('followups.markDone')}
        >
          <Check size={11} /> Fait
        </button>
        <button
          onClick={() => postpone(3)}
          disabled={update.isPending}
          className="inline-flex items-center gap-1 rounded-lg bg-ink/[0.05] px-2.5 py-1.5 text-[11.5px] font-700 text-ink/65 transition hover:bg-ink/[0.10] disabled:opacity-50"
          title={t("followups.postpone3d")}
        >
          <CalendarPlus size={11} /> +3j
        </button>
        <button
          onClick={() => onOpen(p)}
          className="rounded-lg bg-ink/[0.05] p-1.5 text-ink/55 transition hover:bg-pink/10 hover:text-pink"
          aria-label={t("followups.openCard")}
          title={t("followups.openCard")}
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </motion.div>
  );
}

function formatDue(iso: string): string {
  const d = new Date(iso);
  const days = Math.floor((d.getTime() - Date.now()) / 86_400_000);
  if (days < -1) return `il y a ${Math.abs(days)} jours`;
  if (days === -1) return 'hier';
  if (days === 0) return "aujourd'hui";
  if (days === 1) return 'demain';
  if (days <= 7) return `dans ${days} jours`;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}
